import { onShowLinkDialogClicked } from '../../util/commandActions';
import {
    createMailAppButton,
    createMailAppCompoundMenuButton,
} from '../../util/createMailRibbonButton';
import { getSvgIconForFile } from '../../util/getSvgIconForFile';
import { ComposeUniqueState } from 'owa-attachment-filepicker';
import { attachmentMultiMenuEntry } from 'owa-attachment-multi-menu-actions';
import {
    lazyGetAttachmentSuggestionsTraceId,
    ReplyWithAttachmentSuggestion,
    ReplyWithEndpointType,
    replyWithMenuHeaderText,
} from 'owa-attachment-suggestions';
import type { AttachmentSuggestionsMenuItems } from 'owa-attachment-suggestions';
import { getAttachmentAccessDate } from 'owa-attachment-suggestions/lib/utils/getAttachmentAccessDate';
import {
    getActionSourceFromCommandingViewMode,
    getKeytipString,
    constructKeytip,
} from 'owa-command-ribbon';
import CloudArrowUp from 'owa-fluent-icons-svg/lib/icons/CloudArrowUpRegular';
import Cloud from 'owa-fluent-icons-svg/lib/icons/CloudRegular';
import Laptop from 'owa-fluent-icons-svg/lib/icons/LaptopRegular';
import loc from 'owa-localize';
import getComposeUniqueState from 'owa-mail-compose-actions/lib/utils/getComposeUniqueState';
import getSuggestedAttachmentMessageFromComposeViewState from 'owa-mail-compose-actions/lib/utils/getSuggestedAttachmentMessageFromComposeViewState';
import AttachmentMultiPicker from 'owa-mail-compose-view/lib/types/AttachmentMultiPicker';
import { onAttachmentClicked } from 'owa-mail-compose-view/lib/utils/attachButtonMenuItemBuilderUtils';
import { getAttachmentClickHandlerMap } from 'owa-mail-compose-view/lib/utils/getAttachmentClickHandlerMap';
import { MailRibbonControlId, MailRibbonTabId } from 'owa-ribbon-ids';
import { ControlInfo, getControlInfo } from 'owa-mail-ribbon-utils';
import { getComposeRibbonId } from 'owa-editor/lib/utils/getComposeRibbonId';
import { getMainWindowComposeViewState } from 'owa-mail-compose-actions/lib/selectors/getMainWindowComposeViewState';
import getAttachmentPolicy from 'owa-session-store/lib/utils/getAttachmentPolicy';
import logAttachButtonClickedDatapoint from 'owa-mail-compose-view/lib/utils/logAttachButtonClickedDatapoint';
import isSmimeEnabledInViewState from 'owa-smime/lib/utils/isSmimeEnabledInViewState';
import type { MenuSection } from '@1js/acui-menu/lib/components/MenuDefinition';
import type { MenuItemControlProps } from '@1js/acui-ribbon-like/lib/UISurfaces/controls/Menu';
import type { ComposeViewState } from 'owa-mail-compose-store';
import type { AttachmentMultiMenuMap } from 'owa-mail-compose-view/lib/types/AttachmentMultiMenuMap';
import {
    getDefaultButtonsList,
    getSmimeAttachButtonsList,
} from 'owa-mail-compose-view/lib/utils/getAttachmentCommandItems';

export interface AttachmentFlyoutSections {
    menuSections: MenuSection[];
    onMenuOpened: () => void;
    onMenuDismissed: () => void;
}

export default function getAttachFileSections(
    attachmentPickers: AttachmentMultiPicker,
    keytipTabId: MailRibbonTabId | undefined,
    suggestionsGetter?: AttachmentSuggestionsMenuItems,
    popoutComposeViewState?: ComposeViewState
): AttachmentFlyoutSections {
    const viewState: ComposeViewState | undefined =
        popoutComposeViewState ?? getMainWindowComposeViewState();
    const isSmimeCompose: boolean =
        viewState != undefined && isSmimeEnabledInViewState(viewState.smimeViewState);
    const attachmentPolicy = getAttachmentPolicy();
    const referenceAttachmentsEnabled = attachmentPolicy?.ReferenceAttachmentsEnabled;
    const referenceAttachmentsSupported: boolean =
        (viewState != undefined &&
            referenceAttachmentsEnabled &&
            getComposeUniqueState(viewState) === ComposeUniqueState.None) ??
        false;

    return {
        menuSections: getAttachMenuSections(
            isSmimeCompose,
            referenceAttachmentsEnabled,
            referenceAttachmentsSupported,
            attachmentPickers,
            keytipTabId,
            suggestionsGetter,
            popoutComposeViewState
        ),
        onMenuOpened: () => {
            const viewState: ComposeViewState | undefined =
                popoutComposeViewState ?? getMainWindowComposeViewState();
            if (viewState && suggestionsGetter) {
                logAttachButtonClickedDatapoint(viewState.referenceItemId?.Id);

                suggestionsGetter.onMenuOpened(
                    !isSmimeCompose,
                    viewState.referenceItemId,
                    viewState.composeId,
                    (isSmimeCompose
                        ? getSmimeAttachButtonsList()
                        : getDefaultButtonsList(!referenceAttachmentsSupported)
                    ).join(','),
                    referenceAttachmentsSupported,
                    true /* classicAttachmentsSupported */,
                    getSuggestedAttachmentMessageFromComposeViewState(viewState),
                    viewState.suggestedAttachmentSessionId
                );
            }
        },
        onMenuDismissed: () => {
            const viewState: ComposeViewState | undefined =
                popoutComposeViewState ?? getMainWindowComposeViewState();
            if (viewState && suggestionsGetter) {
                suggestionsGetter.onMenuDismissed(
                    !isSmimeCompose,
                    viewState.referenceItemId,
                    referenceAttachmentsSupported,
                    true /* classicAttachmentsSupported */
                );
            }
        },
    };
}

function getAttachMenuSections(
    isSmimeCompose: boolean,
    referenceAttachmentsEnabled: boolean | undefined,
    referenceAttachmentsSupported: boolean,
    attachmentPickers: AttachmentMultiPicker,
    keytipTabId: MailRibbonTabId | undefined,
    suggestionsGetter?: AttachmentSuggestionsMenuItems,
    popoutComposeViewState?: ComposeViewState
): MenuSection[] {
    const sections: MenuSection[] = [];
    const viewState: ComposeViewState | undefined =
        popoutComposeViewState ?? getMainWindowComposeViewState();

    if (viewState === undefined) {
        return sections;
    }

    const attachmentPolicy = getAttachmentPolicy();
    const classicAttachmentsEnabled = attachmentPolicy?.ClassicAttachmentsEnabled;
    const topSectionControls: MenuItemControlProps[] = [];
    const bottomSectionControls: MenuItemControlProps[] = [];

    if (classicAttachmentsEnabled) {
        getClassicAttachmentControls(
            attachmentPickers,
            topSectionControls,
            keytipTabId,
            popoutComposeViewState
        );
    }

    if (!isSmimeCompose) {
        if (classicAttachmentsEnabled || referenceAttachmentsEnabled) {
            getOnlineAttachmentControls(
                attachmentPickers,
                topSectionControls,
                keytipTabId,
                popoutComposeViewState
            );
        }

        if (
            attachmentMultiMenuEntry.uploadAndShareFromCloud.isEnabled(
                !!viewState.attachmentWell?.groupId
            ) &&
            referenceAttachmentsSupported
        ) {
            getUploadAndShareControls(
                attachmentPickers,
                topSectionControls,
                keytipTabId,
                popoutComposeViewState
            );
        }

        // Attachment suggestions (replyWith + MRU)
        if (suggestionsGetter) {
            createAttachmentSuggestions(
                suggestionsGetter.getSuggestions(
                    true /*shouldUseSuggestions*/,
                    viewState.referenceItemId,
                    referenceAttachmentsSupported,
                    classicAttachmentsEnabled == true,
                    viewState.suggestedAttachmentSessionId
                ),
                attachmentPickers.targetWindow,
                bottomSectionControls,
                popoutComposeViewState
            );
        }
    }

    const controlInfo: ControlInfo = getControlInfo(MailRibbonControlId.ShowLinkDialog);

    topSectionControls.push(
        createMailAppButton(
            getComposeRibbonId(MailRibbonControlId.ShowLinkDialog, popoutComposeViewState),
            controlInfo.text,
            controlInfo.tooltip,
            popoutComposeViewState != undefined /*isPopout*/,
            () => {
                const viewState: ComposeViewState | undefined =
                    popoutComposeViewState ?? getMainWindowComposeViewState();

                if (viewState) {
                    onShowLinkDialogClicked(viewState, attachmentPickers.targetWindow);
                }
            },
            constructKeytip(
                keytipTabId
                    ? [
                          getKeytipString(keytipTabId),
                          getKeytipString(MailRibbonControlId.AttachFile),
                      ]
                    : [getKeytipString(MailRibbonControlId.AttachFile)],
                getKeytipString(MailRibbonControlId.ShowLinkDialog)
            ),
            controlInfo.iconName
        )
    );

    if (topSectionControls.length > 0) {
        sections.push({
            controls: topSectionControls,
        });
    }

    if (bottomSectionControls.length > 0) {
        sections.push({
            title: loc(replyWithMenuHeaderText),
            controls: bottomSectionControls,
        });
    }

    return sections;
}

function getClassicAttachmentControls(
    attachmentPickers: AttachmentMultiPicker,
    controls: MenuItemControlProps[],
    keytipTabId: MailRibbonTabId | undefined,
    popoutComposeViewState?: ComposeViewState
): MenuItemControlProps[] {
    controls.push(
        createMailAppButton(
            getComposeRibbonId(MailRibbonControlId.AttachFileFromComputer, popoutComposeViewState),
            attachmentMultiMenuEntry.computer.text(),
            attachmentMultiMenuEntry.computer.ariaLabel
                ? attachmentMultiMenuEntry.computer.ariaLabel()
                : undefined,
            popoutComposeViewState != undefined /*isPopout*/,
            async () => {
                const viewState: ComposeViewState | undefined =
                    popoutComposeViewState ?? getMainWindowComposeViewState();

                if (viewState) {
                    const clickHandlerMap: AttachmentMultiMenuMap<
                        () => void
                    > = getAttachmentClickHandlerMap(
                        attachmentPickers,
                        viewState,
                        attachmentMultiMenuEntry,
                        getActionSourceFromCommandingViewMode()
                    );

                    clickHandlerMap.computer();

                    const getAttachmentSuggestionsTraceId = await lazyGetAttachmentSuggestionsTraceId.import();
                    const traceId = getAttachmentSuggestionsTraceId(viewState.referenceItemId);

                    if (traceId) {
                        attachmentMultiMenuEntry.computer.logUsage(
                            getActionSourceFromCommandingViewMode(),
                            traceId
                        );
                    }
                }
            },
            constructKeytip(
                keytipTabId
                    ? [
                          getKeytipString(keytipTabId),
                          getKeytipString(MailRibbonControlId.AttachFile),
                      ]
                    : [getKeytipString(MailRibbonControlId.AttachFile)],
                getKeytipString(MailRibbonControlId.AttachFileFromComputer)
            ),
            Laptop,
            undefined /*iconColor*/,
            undefined /*styles*/,
            {
                disabled: !attachmentMultiMenuEntry.computer.isEnabled(),
            }
        )
    );

    return controls;
}

function getOnlineAttachmentControls(
    attachmentPickers: AttachmentMultiPicker,
    controls: MenuItemControlProps[],
    keytipTabId: MailRibbonTabId | undefined,
    popoutComposeViewState?: ComposeViewState
): MenuItemControlProps[] {
    controls.push(
        createMailAppButton(
            getComposeRibbonId(
                MailRibbonControlId.AttachFileFromOnlineLocation,
                popoutComposeViewState
            ),
            attachmentMultiMenuEntry.onlineLocation.text(),
            attachmentMultiMenuEntry.onlineLocation.ariaLabel
                ? attachmentMultiMenuEntry.onlineLocation.ariaLabel()
                : undefined,
            popoutComposeViewState != undefined /*isPopout*/,
            async () => {
                const viewState: ComposeViewState | undefined =
                    popoutComposeViewState ?? getMainWindowComposeViewState();

                if (viewState) {
                    const clickHandlerMap: AttachmentMultiMenuMap<
                        () => void
                    > = getAttachmentClickHandlerMap(
                        attachmentPickers,
                        viewState,
                        attachmentMultiMenuEntry,
                        getActionSourceFromCommandingViewMode()
                    );

                    clickHandlerMap.onlineLocation();
                    const getAttachmentSuggestionsTraceId = await lazyGetAttachmentSuggestionsTraceId.import();
                    const traceId = getAttachmentSuggestionsTraceId(viewState.referenceItemId);
                    if (traceId) {
                        attachmentMultiMenuEntry.onlineLocation.logUsage(
                            getActionSourceFromCommandingViewMode(),
                            traceId
                        );
                    }
                }
            },
            constructKeytip(
                keytipTabId
                    ? [
                          getKeytipString(keytipTabId),
                          getKeytipString(MailRibbonControlId.AttachFile),
                      ]
                    : [getKeytipString(MailRibbonControlId.AttachFile)],
                getKeytipString(MailRibbonControlId.AttachFileFromOnlineLocation)
            ),
            Cloud,
            undefined /*iconColor*/,
            undefined /*styles*/,
            {
                disabled: !attachmentMultiMenuEntry.onlineLocation.isEnabled(),
            }
        )
    );

    return controls;
}

function getUploadAndShareControls(
    attachmentPickers: AttachmentMultiPicker,
    controls: MenuItemControlProps[],
    keytipTabId: MailRibbonTabId | undefined,
    popoutComposeViewState?: ComposeViewState
): MenuItemControlProps[] {
    controls.push(
        createMailAppButton(
            getComposeRibbonId(MailRibbonControlId.AttachFileFromUpload, popoutComposeViewState),
            attachmentMultiMenuEntry.uploadAndShareFromCloud.text(),
            attachmentMultiMenuEntry.uploadAndShareFromCloud.ariaLabel
                ? attachmentMultiMenuEntry.uploadAndShareFromCloud.ariaLabel()
                : undefined,
            popoutComposeViewState != undefined /*isPopout*/,
            async () => {
                const viewState: ComposeViewState | undefined =
                    popoutComposeViewState ?? getMainWindowComposeViewState();

                if (viewState) {
                    const clickHandlerMap: AttachmentMultiMenuMap<
                        () => void
                    > = getAttachmentClickHandlerMap(
                        attachmentPickers,
                        viewState,
                        attachmentMultiMenuEntry,
                        getActionSourceFromCommandingViewMode()
                    );

                    clickHandlerMap.uploadAndShareFromCloud();

                    const getAttachmentSuggestionsTraceId = await lazyGetAttachmentSuggestionsTraceId.import();
                    const traceId = getAttachmentSuggestionsTraceId(viewState.referenceItemId);
                    if (traceId) {
                        attachmentMultiMenuEntry.uploadAndShareFromCloud.logUsage(
                            getActionSourceFromCommandingViewMode(),
                            traceId
                        );
                    }
                }
            },
            constructKeytip(
                keytipTabId
                    ? [
                          getKeytipString(keytipTabId),
                          getKeytipString(MailRibbonControlId.AttachFile),
                      ]
                    : [getKeytipString(MailRibbonControlId.AttachFile)],
                getKeytipString(MailRibbonControlId.AttachFileFromUpload)
            ),
            CloudArrowUp,
            undefined /*iconColor*/,
            undefined /*styles*/,
            {
                disabled: !attachmentMultiMenuEntry.uploadAndShareFromCloud.isEnabled(),
            }
        )
    );

    return controls;
}

const TRUNCATION_SEPARATOR: string = '...';
const MAX_FILENAME_LENGTH: number = 35;
const MAX_BREADCRUMBS_LENGTH: number = 50;

function stripFileNameExtension(fileName: string, dottedFileExtension: string): string {
    // If file name contains file extension, remove it. We'll add it separately.
    return fileName.indexOf(dottedFileExtension) >= 0
        ? fileName.replace(dottedFileExtension, '')
        : fileName;
}

function getCleanFileNameFromSuggestion(suggestion: ReplyWithAttachmentSuggestion): string {
    const dottedFileExtension: string = '.' + suggestion.FileExtension;

    return stripFileNameExtension(suggestion.FileName, dottedFileExtension) + dottedFileExtension;
}

function getMiddleTruncateFileNameFromSuggestion(
    suggestion: ReplyWithAttachmentSuggestion
): string {
    return middleTruncateString(getCleanFileNameFromSuggestion(suggestion), MAX_FILENAME_LENGTH);
}

function middleTruncateString(str: string, maxLength: number): string {
    // Do not truncate the string if it contains MAX_FILENAME_LENGTH characters or fewer.
    if (str.length <= maxLength) {
        return str;
    }

    const charsToShow: number = maxLength - TRUNCATION_SEPARATOR.length;
    const frontChars: number = Math.ceil(charsToShow / 2);
    const backChars: number = Math.floor(charsToShow / 2);
    return str.substr(0, frontChars) + TRUNCATION_SEPARATOR + str.substr(str.length - backChars);
}

function getSecondaryTextForAttachmentSuggestion(suggestion: ReplyWithAttachmentSuggestion) {
    if (
        suggestion.Endpoint === ReplyWithEndpointType.MRUClassic ||
        suggestion.Endpoint === ReplyWithEndpointType.MRUClassicContextual
    ) {
        return suggestion.BreadcrumbLocation
            ? middleTruncateString(suggestion.BreadcrumbLocation, MAX_BREADCRUMBS_LENGTH)
            : '';
    }

    return getAttachmentAccessDate(suggestion);
}

function createAttachmentSuggestions(
    suggestions: ReplyWithAttachmentSuggestion[],
    targetWindow: Window,
    controls: MenuItemControlProps[],
    popoutComposeViewState?: ComposeViewState
): MenuItemControlProps[] {
    suggestions.forEach(suggestion => {
        controls.push(
            createMailAppCompoundMenuButton(
                getComposeRibbonId(suggestion.Id, popoutComposeViewState),
                getMiddleTruncateFileNameFromSuggestion(suggestion),
                suggestion.Tooltip,
                getSecondaryTextForAttachmentSuggestion(suggestion),
                () => {
                    const viewState: ComposeViewState | undefined =
                        popoutComposeViewState ?? getMainWindowComposeViewState();

                    if (viewState) {
                        onAttachmentClicked(
                            suggestion,
                            suggestion.Position ?? 0,
                            viewState,
                            targetWindow
                        );
                    }
                },
                getSvgIconForFile(getCleanFileNameFromSuggestion(suggestion))
            )
        );
    });

    return controls;
}
